/**
 * Application Routes
 * Centralized routing configuration
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Layout Components
import AppLayout from '../components/layout/AppLayout';

// Auth Pages
import Login from '../pages/auth/Login';
import Signup from '../pages/auth/Signup';
import EmailVerification from '../pages/auth/EmailVerification';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';
import PasswordResetRedirect from '../pages/auth/PasswordResetRedirect';

// Application Components
import EditApplicationForm from '../components/applications/EditApplicationForm';

// Public Payment Components
import PublicInvoicePaymentView from '../components/payments/PublicInvoicePaymentView';
import PaymentSuccessPage from '../components/payments/PaymentSuccessPage';
import PaypalReturnPage from '../components/payments/PaypalReturnPage';

// Dashboard Pages
import AdminDashboardContainer from '../pages/dashboard/AdminDashboardContainer';
import UserDashboard from '../pages/dashboard/UserDashboard';

// Profile Pages
import ProfileManagement from '../pages/profile/ProfileManagement';

// Member Pages

// Admin Management Pages
import ApplicationManagementPage from '../pages/admin/ApplicationManagementPage';
import MailChimpManagementPage from '../pages/admin/MailChimpManagementPage';
import FinancialPeriodsPage from '../pages/admin/FinancialPeriodsPage';
import DistributorsPage from '../pages/admin/DistributorsPage';
import GoogleSheetsPage from '../pages/admin/GoogleSheetsPage';
import EnhancedInvoiceManagementPage from '../pages/admin/EnhancedInvoiceManagementPage';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, isLoading } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole) {
    const userRole = user?.user_role || user?.role;
    if (userRole !== requiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute requiredRole="Superuser">
      {children}
    </ProtectedRoute>
  );
};


const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/email-verification" element={<EmailVerification />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/api/auth/password-reset/" element={<PasswordResetRedirect />} />
      <Route path="/edit-application/:token" element={<EditApplicationForm />} />
      <Route path="/invoice/pay/:token" element={<PublicInvoicePaymentView />} />
      <Route path="/invoice/payment-success/:token" element={<PaymentSuccessPage />} />
      <Route path="/invoice/paypal-return/:token" element={<PaypalReturnPage />} />

      {/* Protected Routes */}
      <Route path="/" element={<AppLayout />}>
        {/* Dashboard Routes */}
        <Route
          path="admin-dashboard"
          element={
            <AdminRoute>
              <AdminDashboardContainer />
            </AdminRoute>
          }
        />
        <Route
          path="user-dashboard"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />

        {/* Profile Routes */}
        <Route
          path="profile"
          element={
            <ProtectedRoute>
              <ProfileManagement />
            </ProtectedRoute>
          }
        />


        {/* Admin Management Routes */}
        <Route
          path="admin/applications"
          element={
            <AdminRoute>
              <ApplicationManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/mailchimp"
          element={
            <AdminRoute>
              <MailChimpManagementPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/financial-periods"
          element={
            <AdminRoute>
              <FinancialPeriodsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/distributors"
          element={
            <AdminRoute>
              <DistributorsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/google-sheets"
          element={
            <AdminRoute>
              <GoogleSheetsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/enhanced-invoices"
          element={
            <AdminRoute>
              <EnhancedInvoiceManagementPage />
            </AdminRoute>
          }
        />


        {/* Default redirect */}
        <Route index element={<Navigate to="/login" replace />} />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;
